import React, {
  Fragment,
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import { setBundlePdpAovSkus } from '../../../slices/pdp';
import AnimatedProductCard from '../../../components/common/AnimatedProductCard';
import AnimatedVariantSelection from '../../../components/common/AnimatedVariantSelection';
import { getBundleItemVariantSelectionAttributesUpperTitle } from '../../bundlePdp/BundleOptions';
import { productTypeToSingularProductNameConverter } from '../../../utils/product';
import {
  getBundlePdpAovItemsToRender,
  getCheapestVariant,
  getTargetProductWithAllVariantsBySKU,
} from '../../../utils';
import { buildTextFromVariantAttributes } from '../VariantSelection/utils';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';
import messages from './messages';
import styles from './FixedBundlePdpAOV.module.scss';
import {
  KID_SHEETS_COLOR_KEY, KID_SHEETS_KEY, SOFTSTRETCH_SHEETS_KEY, VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
} from '../VariantSelection/constants';
import {
  MATTRESSES_PRODUCT_TYPE,
  PILLOWS_PRODUCT_TYPE,
  SIZE_ATTRIBUTE_NAME,
  TWIN_SIZE_KEY,
  TWIN_XL_SIZE_KEY,
} from '../../../utils/common/constants';
import usePrevious from '../../../hooks/usePrevious';
import useTealium from '../../../hooks/useTealium';

const getDefaultVariant = (variants) => {
  const masterVariant = variants.find((variant) => variant.masterVariant);
  return masterVariant || variants.filter((variant) => !variant.isOutOfStock)[0];
};

const bundleHasMattressAndPillows = (allBundleOptions) =>
  allBundleOptions.some(
    (option) => option.productTypeName?.toLowerCase() === MATTRESSES_PRODUCT_TYPE,
  ) &&
  allBundleOptions.some(
    (option) => option.productTypeName?.toLowerCase() === PILLOWS_PRODUCT_TYPE,
  );

// Only normalizes when targetSku is itself one of the bundle's mattress variants —
// that's the only case where the mattress size is actually known. Any other bundle
// shape (e.g. the main product is the Sheets, not the Mattress) is left untouched
// rather than guessing a size, since a guess can't be trusted to be twin/twinXL.
const normalizeBundlePillowQuantity = (bundleItems, allBundleOptions, targetSku) => {
  const mattressVariants = allBundleOptions
    .filter((option) => option.productTypeName?.toLowerCase() === MATTRESSES_PRODUCT_TYPE)
    .flatMap((option) => option.variants || []);

  const selectedMattressSizeKey = mattressVariants
    .find((variant) => variant.sku === targetSku)
    ?.attributes
    ?.find((attribute) => attribute.name === SIZE_ATTRIBUTE_NAME)
    ?.key;

  if (!selectedMattressSizeKey) {
    return bundleItems;
  }

  const normalizedPillowQuantity =
    selectedMattressSizeKey === TWIN_SIZE_KEY || selectedMattressSizeKey === TWIN_XL_SIZE_KEY
      ? 1
      : 2;

  return bundleItems.map((bundleItem) => ({
    ...bundleItem,
    options: bundleItem.options.map((option) => ({
      ...option,
      quantity: option.productTypeName?.toLowerCase() === PILLOWS_PRODUCT_TYPE
        ? normalizedPillowQuantity
        : option.quantity,
    })),
  }));
};

const getSkusFromSelectedVariantsPerBundleItem = ({
  selectedVariantsPerBundleItem,
}) => {
  if (selectedVariantsPerBundleItem) {
    return Object.entries(selectedVariantsPerBundleItem).reduce(
      (acc, cur) => [...acc, ...cur[1]],
      [],
    );
  }
  return [];
};

const getPriceAndDiscountFromSelectedVariantsPerBundleItem = ({
  bundleItems,
  selectedVariantsPerBundleItem,
}) => {
  if (selectedVariantsPerBundleItem) {
    const allBundleItemsVariants = bundleItems?.flatMap((bundleItem) =>
      bundleItem.options?.flatMap(
        (bundleItemOption) => bundleItemOption.variants,
      ),
    );

    const selectedSkus = Object.entries(selectedVariantsPerBundleItem).flatMap(
      (selectedVariant) => selectedVariant[1],
    );

    const selectedBundlePdpAOVVariants = selectedSkus.map(
      (selectedBundleAOVSku) =>
        allBundleItemsVariants?.find(
          (variant) => variant.sku === selectedBundleAOVSku,
        ),
    );
    return selectedBundlePdpAOVVariants.reduce(
      (acc, cur) => ({
        price: {
          ...acc.price,
          centAmount: acc.price.centAmount + cur?.price?.centAmount ?? 0,
        },
        discount: {
          ...acc.price,
          centAmount: acc.discount.centAmount + cur?.discount?.centAmount ?? 0,
        },
      }),
      {
        price: {
          currencyCode: 'USD',
          fractionDigits: 2,
          type: 'centPrecision',
          centAmount: 0,
        },
        discount: {
          currencyCode: 'USD',
          fractionDigits: 2,
          type: 'centPrecision',
          centAmount: 0,
        },
      },
    );
  }
  return [];
};

const getBundlesForSelectedSKU = (allAovs, sku) => {
  const result = allAovs?.find(
    (aov) =>
      aov.targetSku === sku && aov.bundles?.showOnPdpV2,
  );

  if (result) {
    return result.bundles;
  }
  return {};
};

const getBundleItems = (
  bundlesParam,
  targetSku,
) => {
  let bundleItems = [];
  if (bundlesParam.items) {
    bundleItems = getBundlePdpAovItemsToRender(
      bundlesParam.items,
      targetSku,
    );

    // If Twin/TwinXL is selected and the bundle is softstretch sheet, it should be replaced with kid sheet.
    bundleItems = bundleItems.map((bundleItem) => {
      const softStreshSheet = bundleItem.options
        .find((option) => option.productKey === SOFTSTRETCH_SHEETS_KEY);
      const kidSheet = bundleItem.options
        .find((option) => option.productKey === KID_SHEETS_KEY);

      return {
        ...bundleItem,
        options: softStreshSheet && kidSheet
          ? [
            {
              ...softStreshSheet,
              variants: [
                ...softStreshSheet.variants,
                ...kidSheet.variants.filter((variant) =>
                  variant.attributes
                    .find((attribute) => attribute.name === VARIANT_ATTRIBUTE_NAME_COLOR_CODE
                     && KID_SHEETS_COLOR_KEY.includes(attribute.key),
                    ),
                ),
              ],
            },
          ]
          : bundleItem.options,
      };
    });

    // Bundles that include a mattress should only include one pillow when that
    // mattress is Twin/TwinXL. This must run last so no earlier transform can
    // be overwritten by it, nor overwrite it.
    const allBundleOptions = bundlesParam.items.flatMap((bundleItem) =>
      bundleItem.options,
    );

    if (bundleHasMattressAndPillows(allBundleOptions)) {
      bundleItems = normalizeBundlePillowQuantity(bundleItems, allBundleOptions, targetSku);
    }
  }

  return bundleItems;
}

const FixedBundlePdpAOV = () => {
  const { locale, formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [selectedVariantsPerBundleItem, setSelectedVariantsPerBundleItem] =
    useState({});

  const { aovs: allAovs, product } = useDrupalSettings();

  const selectedBundlePdpAOVSKUs = useSelector(
    (state) => state.pdp.selectedBundlePdpAOVSKUs,
  );
  const selectedTargetProductSKU = useSelector(
    (state) => state.pdp.selectedSKU,
  );
  const prevSelectedTargetProductSKU = usePrevious(selectedTargetProductSKU);
  const hasAnItemSelected = selectedBundlePdpAOVSKUs.length > 0;

  const { publish } = drupalPubSubService;

  const bundles = useMemo(() =>
    getBundlesForSelectedSKU(allAovs, selectedTargetProductSKU),
  [allAovs, selectedTargetProductSKU]);

  const prevBundles = useMemo(() =>
    getBundlesForSelectedSKU(allAovs, prevSelectedTargetProductSKU),
  [allAovs, prevSelectedTargetProductSKU]);

  const items = getBundleItems(bundles, selectedTargetProductSKU);
  const prevItems = getBundleItems(prevBundles, prevSelectedTargetProductSKU);

  const mainSelectedSKUhasBundlesV2 = !!allAovs?.find(
    (aov) =>
      aov.targetSku === selectedTargetProductSKU && aov.bundles?.showOnPdpV2,
  );

  const mainSelectedSKUhasBundlesV1 = !!allAovs?.find(
    (aov) =>
      aov.targetSku === selectedTargetProductSKU && aov.bundles?.showOnPdp,
  );

  const newVariantsSelectedForProducts = items.reduce(
    (result, currentItem, itemIndex) => {
      const newSkusPerProduct = currentItem.options.reduce(
        (optionResult, currentOption) => {
          if (currentOption.variants[0].sku === selectedTargetProductSKU) {
            return optionResult;
          }

          const prevColorKey = prevItems?.[itemIndex]?.options?.[0]?.variants
            ?.find((variant) =>
              variant.sku === selectedBundlePdpAOVSKUs[itemIndex] && !variant.isOutOfStock,
            )
            ?.attributes
            ?.find((attribute) => attribute.name === 'color')
            ?.key;

          const defaultVariant = currentOption.variants
            .find((variant) =>
              variant.attributes
                .find((attribute) => attribute.name === 'color' && attribute.key === prevColorKey),
            ) || getDefaultVariant(currentOption.variants);

          return {
            ...optionResult,
            [currentOption.productId]: Array(currentOption.quantity).fill(
              defaultVariant.sku,
            ),
          };
        },
        {},
      );

      return { ...result, ...newSkusPerProduct };
    },
    {},
  );
  const newVariantsSelectedForProductsString = JSON.stringify(newVariantsSelectedForProducts);

  useTealium();

  useEffect(() => {
    if (mainSelectedSKUhasBundlesV2) {
      const variantsSelectedForProducts = JSON.parse(newVariantsSelectedForProductsString);

      if (hasAnItemSelected) {
        const newSelectedskus = getSkusFromSelectedVariantsPerBundleItem({
          selectedVariantsPerBundleItem: variantsSelectedForProducts,
        });
        dispatch(setBundlePdpAovSkus(newSelectedskus));
      }

      setSelectedVariantsPerBundleItem(variantsSelectedForProducts);
    }
  }, [
    dispatch,
    hasAnItemSelected,
    mainSelectedSKUhasBundlesV2,
    newVariantsSelectedForProductsString
  ]);

  const notifyDrupalOfBundleSelection = useCallback(
    (clearValues) => {
      const arrayOfExcludedSkus =
        process.env.PRICE_NOTIFICATION_EXCLUDED_SKUS?.split(',') || [];
      const shouldNotifyDrupal = !arrayOfExcludedSkus.includes(
        selectedTargetProductSKU,
      );
      if (shouldNotifyDrupal) {
        const targetProduct = clearValues
          ? product
          : getTargetProductWithAllVariantsBySKU(
            bundles.items,
            selectedTargetProductSKU,
          );

        const cheapestVariant = getCheapestVariant(targetProduct.variants);
        const safeDiscount = cheapestVariant.discount?.centAmount ?? 0;

        const basePrice =
          (cheapestVariant.price.centAmount + safeDiscount) / 100;
        const discount = safeDiscount / 100;

        publish({
          subscription: 'onPriceChange',
          parameters: {
            base_price: basePrice,
            discount,
          },
        });
      }
    },
    [publish, selectedTargetProductSKU, product, bundles],
  );

  const skus = getSkusFromSelectedVariantsPerBundleItem({
    selectedVariantsPerBundleItem,
  });

  const bundleImage = useMemo(() => {
    const bundleAOVSkus = selectedBundlePdpAOVSKUs?.length ? selectedBundlePdpAOVSKUs : skus;

    if (!bundleAOVSkus?.length) {
      return '';
    }

    const matchedImage = bundles?.images
      ?.find((image) => image.skuCombination.every((pdp, index) => {
        const selectedBundlePDPSku = bundleAOVSkus[index];
        return pdp.skus.includes('all') || pdp.skus.includes(selectedBundlePDPSku);
      }));
    return matchedImage?.imageUrl;
  }, [bundles?.images, selectedBundlePdpAOVSKUs, skus]);

  const selectedVariantsInTheSameOrderAsInBundleOptions = items
    .flatMap((item) => item.options)
    .flatMap(
      (option) =>
        selectedVariantsPerBundleItem[option.productId]?.map(
          (selectedSkuForVariant) =>
            option.variants?.find(
              (variant) => variant.sku === selectedSkuForVariant,
            ),
        ) ?? [],
    );

  const selectedBundlePriceAndDiscount =
    getPriceAndDiscountFromSelectedVariantsPerBundleItem({
      bundleItems: items,
      selectedVariantsPerBundleItem,
    });

  const descriptionWithoutHtml = bundles.description?.[locale]?.replace(
    /(<([^>]+)>)/gi,
    '',
  );

  const badgeDiscount =
    selectedBundlePriceAndDiscount?.discount?.centAmount > 0
      ? Math.floor(
        (selectedBundlePriceAndDiscount?.discount?.centAmount /
          (selectedBundlePriceAndDiscount?.price?.centAmount +
            selectedBundlePriceAndDiscount?.discount?.centAmount)) *
          100,
      )
      : undefined;
  const badgeDiscountText = badgeDiscount
    ? formatMessage(messages.badgeDiscountText, {
      discountPercentage: badgeDiscount.toString(),
    }).toUpperCase()
    : undefined;

  useEffect(() => {
    if (!mainSelectedSKUhasBundlesV2 && !mainSelectedSKUhasBundlesV1) {
      dispatch(setBundlePdpAovSkus([]));
    }
  }, [dispatch, mainSelectedSKUhasBundlesV1, mainSelectedSKUhasBundlesV2]);

  return mainSelectedSKUhasBundlesV2 ? (
    <>
      <AnimatedProductCard
        id={items[0]?.options?.[0]?.productId}
        name={bundles.title?.[locale]}
        title={buildTextFromVariantAttributes({
          template: bundles.title?.[locale],
          variants: selectedVariantsInTheSameOrderAsInBundleOptions,
        })}
        description={buildTextFromVariantAttributes({
          template: descriptionWithoutHtml,
          variants: selectedVariantsInTheSameOrderAsInBundleOptions,
        })}
        badge={badgeDiscountText}
        badgeIsAlwaysVisible={!!badgeDiscount}
        badgeStyle="alternative"
        price={selectedBundlePriceAndDiscount?.price}
        imageURL={bundleImage}
        discount={selectedBundlePriceAndDiscount?.discount}
        displayDiscount={
            selectedBundlePriceAndDiscount?.discount?.centAmount > 0
          }
        onChange={() => {
          if (hasAnItemSelected) {
            dispatch(setBundlePdpAovSkus([]));
            notifyDrupalOfBundleSelection(true);
          } else {
            dispatch(setBundlePdpAovSkus(skus));
            notifyDrupalOfBundleSelection(false);
          }
        }}
        isSelected={hasAnItemSelected}
      />
      {hasAnItemSelected &&
        items.map((item, index) => {
          const key = item.options[0].productId + index;
          const optionElements = item.options
            .filter((option) => !!option.variants.length)
            .map((option) => (
              <div key={option.productId}>
                {[...Array(option.quantity).keys()].map(
                  (_, optionQuantityIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Fragment key={`${option.productId}-${optionQuantityIndex}`}>
                      {option?.quantity > 1 && (
                      <div className={styles['quantity-title']}>
                        {getBundleItemVariantSelectionAttributesUpperTitle({
                          bundleItem: {
                            quantity: option?.quantity,
                            product: {
                              productType:
                                option?.productTypeName?.toLowerCase(),
                            },
                          },
                          index: optionQuantityIndex,
                        })}
                      </div>
                      )}
                      <AnimatedVariantSelection
                        variants={option.variants}
                        isShowing
                        defaultSelectedSKU={selectedBundlePdpAOVSKUs[index]}
                        isShowingDiscountedPrices
                        onSelectVariant={(variant) => {
                          const newVariant = {
                            ...selectedVariantsPerBundleItem,
                            [option.productId]: [
                              ...(
                                selectedVariantsPerBundleItem[
                                  option.productId
                                ] ?? []
                              ).filter(
                                (__, selectedSkuIndex) =>
                                  selectedSkuIndex < optionQuantityIndex,
                              ),
                              variant.sku,
                              ...(
                                selectedVariantsPerBundleItem[
                                  option.productId
                                ] ?? []
                              ).filter(
                                (__, selectedSkuIndex) =>
                                  selectedSkuIndex > optionQuantityIndex,
                              ),
                            ],
                          };
                          setSelectedVariantsPerBundleItem(
                            newVariant,
                          );
                          const newBundlePdpAovSkus =
                            getSkusFromSelectedVariantsPerBundleItem({
                              selectedVariantsPerBundleItem: newVariant,
                            });
                          dispatch(setBundlePdpAovSkus(newBundlePdpAovSkus));
                        }}
                        attributeTitlePrefix={
                          option.productTypeName
                            ? productTypeToSingularProductNameConverter(
                              option.productTypeName?.toLowerCase(),
                            )
                            : undefined
                        }
                        productKey={option.productKey}
                      />
                    </Fragment>
                  ),
                )}
              </div>
            ));
          return <div key={key}>{optionElements}</div>;
        })
      }
    </>
  ) : null;
};

export default FixedBundlePdpAOV;
